/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion */
import * as React from 'react';

import { IAgentSchema, IOfferSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import { ISimilarOffer, TModelType } from '../../store/similar';
import { isArrayWithItems } from '../../utils';

import { SimilarOfferItem } from './components/SimilarOfferItem';
import * as Analytics from '../../ebc/track_similar';
import { getVariants } from '../../utils/ebc';

import * as styles from './SimilarOffers.css';

export interface IDataLayerEvent {
  eventAction: string;
  modelVersion: string;
}

export interface ISimilarOffersProps {
  title: string;
  agent: IAgentSchema | null;
  offer: IOfferSchema;
  similarOffers: ISimilarOffer[];
  withoutCompetitors: boolean | null;
  modelVersion: number | string | null;
  modelType: TModelType;
  user: Analytics.IUserEBC;
  page: Analytics.IPageEBC;

  requestSimilarOffers(offerId: number, publishedUserId: number | null): void;
}

export interface ISimilarOffersState {
  wasShawn: boolean;
}

export class SimilarOffers extends React.Component<ISimilarOffersProps, ISimilarOffersState> {
  private container: HTMLElement | null;
  private productsEbc: Analytics.TProductPartial[] = [];

  public state = { wasShawn: false };

  public componentDidMount() {
    const { offer, requestSimilarOffers } = this.props;
    requestSimilarOffers(offer.cianId, offer.publishedUserId);
    window.addEventListener('scroll', this.handleShowOffers);
  }

  public UNSAFE_componentWillReceiveProps(nextProps: ISimilarOffersProps) {
    if (
      !isArrayWithItems<ISimilarOffer>(this.props.similarOffers) &&
      isArrayWithItems<ISimilarOffer>(nextProps.similarOffers)
    ) {
      if (nextProps.similarOffers.length) {
        this.productsEbc = nextProps.similarOffers.map(
          (similarOffer, index): Analytics.TProductPartial => {
            let variant;

            if (similarOffer.gaVariant) {
              variant = getVariants(similarOffer.gaVariant);
            }

            return {
              id: similarOffer.cianId,
              position: index + 1,
              ownerId: similarOffer.publishedUserId || undefined,
              variant,
            };
          },
        );
      }
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('scroll', this.handleShowOffers);
  }

  public render() {
    const { title, similarOffers } = this.props;

    if (!isArrayWithItems<ISimilarOffer>(similarOffers)) {
      return <div className={styles['empty']} ref={container => (this.container = container)} />;
    }

    return (
      <section className={styles['container']} ref={container => (this.container = container)}>
        <h2 className={styles['title_block']}>{title}</h2>
        <ul className={styles['list']}>
          {similarOffers.map((similarOffer, index) => (
            <SimilarOfferItem
              key={`similarOffer_${index}`}
              offer={similarOffer}
              onClick={this.handleClickOffer}
              position={index + 1}
            />
          ))}
        </ul>
      </section>
    );
  }

  private handleShowOffers = () => {
    const { similarOffers, modelVersion: propsModelVersion, modelType, withoutCompetitors, user, page } = this.props;
    const modelVersion = propsModelVersion ? String(propsModelVersion) : '0';

    const eventAction = withoutCompetitors
      ? Analytics.EtrackOffersShowActions.show_others_offers
      : Analytics.EtrackOffersShowActions.show_similar_offers;

    if (
      // tslint:disable:no-any
      (window as any).dataLayer &&
      !(window as any).dataLayer.find(
        (event: IDataLayerEvent) =>
          // tslint:enable:no-any
          event.eventAction === eventAction && event.modelVersion === modelVersion,
      )
    ) {
      // tslint:disable-next-line:no-non-null-assertion
      const containerPositionMiddle = this.container!.offsetTop + this.container!.offsetHeight / 2;
      const windowScrollPosition = window.pageYOffset + window.innerHeight;

      if (
        windowScrollPosition > containerPositionMiddle &&
        !this.state.wasShawn &&
        isArrayWithItems<ISimilarOffer>(similarOffers)
      ) {
        this.setState({ wasShawn: true });

        Analytics.trackOffersShow({
          action: eventAction,
          label: this.getAnalyticsLabel(modelType, withoutCompetitors),
          products: this.productsEbc,
          modelVersion,
          user,
          page,
        });
      }
    }
  };

  private getAnalyticsLabel = (
    modelType: TModelType,
    withoutCompetitors: boolean | null,
  ): Analytics.EtrackOfferClicksLabels | undefined => {
    if (withoutCompetitors) {
      return undefined;
    }

    switch (modelType) {
      case 'userBased':
        return Analytics.EtrackOfferClicksLabels.user_based;

      case 'itemBased':
        return Analytics.EtrackOfferClicksLabels.offer_based;

      case 'historyBased':
        return Analytics.EtrackOfferClicksLabels.history_based;
    }
  };

  private handleClickOffer = (curOffer: ISimilarOffer, position: number) => {
    const { agent, offer, withoutCompetitors, modelType, modelVersion: propsModelVersion, user, page } = this.props;
    const products: Analytics.TProductPartial[] = [
      {
        id: curOffer.cianId,
        position,
        ownerId: curOffer.publishedUserId || undefined,
        variant: curOffer.gaVariant ? getVariants(curOffer.gaVariant) : [],
      },
    ];
    const modelVersion = String(propsModelVersion || 0);

    Analytics.trackOfferClick({
      action: withoutCompetitors
        ? Analytics.EtrackOfferClicksActions.click_others_offers
        : Analytics.EtrackOfferClicksActions.click_similar_offers,
      label: this.getAnalyticsLabel(modelType, withoutCompetitors),
      products,
      agent,
      offer,
      modelVersion,
      user,
      page,
    });
  };
}
